import { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Typography,
  List,
  Button,
  Card,
  Popover,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { CheckCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import TeamsPlanReview from './TeamsPlanReview';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '@frontegg/react';
import { getSubscription } from '../../store/actions/payments';
import { analytics } from 'src/services';

const { Title, Text } = Typography;

export const plans = {
  free: {
    name: 'Free',
    price: 0,
    features: [
      {
        feature: 'Unlimited encounters',
        description: 'Create unlimited encounters with Aura.',
      },
      {
        feature: 'Magic Edit',
        description:
          'Refine your generated note with advanced AI customization options',
      },
      {
        feature: 'Pre-charting',
        description:
          'Dictate pre-visit notes that seamlessly merge into your visit notes',
      },
      {
        feature: 'After visit summary',
        description:
          'AI prepares a simplified visit summary for patient handouts',
      },
      {
        feature: 'Data Retention',
        description: '21 Days',
      },
    ],
  },
  pro: {
    name: 'Pro',
    price: 49.99,
    features: [
      {
        feature: 'Everything in Free',
        description: 'Access all Free features.',
      },
      {
        feature: 'Template Exchange',
        description: 'Share and use note templates with other Aura Pro users.',
      },
      {
        feature: 'Medical Coding (ICD-10 codes)',
        description: 'Automated assistance for accurate medical coding.',
      },
      {
        feature: 'Dictation',
        description: 'Voice-to-text functionality for efficient note-taking.',
      },
      {
        feature: 'Patient History Context',
        description:
          "AI considers patient's medical history for more relevant assistance.",
      },
      {
        feature: 'Memory',
        description: 'AI adapts to your personal note-taking style over time.',
      },
      {
        feature: 'Show Source',
        description: 'View the sources of information used by the AI.',
      },
      {
        feature: 'Auto Template',
        description:
          'Automatically generate custom note templates by uploading patient notes.',
      },
      {
        feature: 'Live Chat Support',
        description: 'Get help from the Insight health team in real-time.',
      },
      {
        feature: 'Create Encounters On Behalf Of',
        description: 'Create encounters on behalf of other users.',
      },
      {
        feature: 'Data Retention',
        description: '30, 60 or 90 Days',
      },
      {
        feature: 'Zoom Integration for Telehealth Encounters',
        description:
          'AI bot joins Zoom calls to automatically record and transcribe patient encounters.',
      },
    ],
  },
  teams: {
    name: 'Teams',
    price: 99.99,
    features: [
      {
        feature: 'Everything in Pro',
        description: 'Access all Pro features.',
      },
      {
        feature: 'EHR Integration (with one-time fee)',
        description: 'Connect your EHR to Aura Pro for seamless integration.',
      },
      {
        feature: 'Admin users',
        description: 'Manage users and their permissions.',
      },
      {
        feature: 'Enhanced Support',
        description: 'Get priority support from the Aura team.',
      },
      {
        feature: 'Custom Template (By MDs)',
        description:
          'Specialty-specific templates curated by our Chief Medical Officers',
      },
      {
        feature: 'Data Retention',
        description: '30, 60 or 90 Days',
      },
    ],
  },
};

export const PlanSelection = ({
  handleContinue,
  setPlan,
  billingCycle,
  setBillingCycle,
  quantity,
  setQuantity,
}) => {
  const [showTeamsDetails, setShowTeamsDetails] = useState(false);
  const {
    practitionerListLoading: _practitionerListLoading,
    practitionerList,
  } = useSelector((state) => state?.practitionerState);
  const { subscription } = useSelector((state) => state.paymentsState);
  const { user } = useAuth();
  const dispatch = useDispatch();
  const numPractitioners = practitionerList?.length;
  const getPlanPrice = (basePrice, isAnnual) => {
    return isAnnual ? Math.ceil(basePrice * 0.8) : basePrice;
  };

  const currentPlan = subscription?.plan_type;

  const getTotalPaidUsers = () => {
    return (
      practitionerList?.filter((practitioner) =>
        practitioner.frontegg_roles?.some((role) => role.is_paid)
      ).length || 0
    );
  };

  const shouldDisablePlan = (planKey) => {
    if (planKey === 'free') {
      return true;
    }
    if (currentPlan === planKey) {
      return true;
    }
    if (currentPlan === 'free' && planKey === 'pro' && numPractitioners > 1) {
      return true;
    }
    return false;
  };

  const disablePlanTooltip = (planKey) => {
    if (currentPlan === planKey) {
      return 'This is your current plan.';
    }
    if (currentPlan === 'free' && planKey === 'pro' && numPractitioners > 1) {
      return 'Pro plan is not available for accounts with multiple users. Please remove them or upgrade to Teams plan.';
    }
    return null;
  };

  const handlePlanSelection = (planKey) => {
    analytics.track(`Clicked ${planKey} Plan In Plan Selection`, {
      email: user?.email,
    });
    setPlan(planKey);
    if (planKey === 'teams') {
      setShowTeamsDetails(false);
    }
  };

  const handleGetPlan = (planKey, e) => {
    e.stopPropagation();

    analytics.track(`Clicked Get ${planKey} Plan In Plan Selection`, {
      email: user?.email,
    });

    if (planKey === 'teams') {
      setShowTeamsDetails(true);
    } else {
      handleContinue({
        plan: planKey,
        billingCycle: billingCycle,
        quantity: quantity,
      });
    }
  };

  useEffect(() => {
    dispatch(getSubscription());
  }, [dispatch]);

  if (showTeamsDetails) {
    return (
      <TeamsPlanReview
        plan={plans.teams}
        onBack={() => setShowTeamsDetails(false)}
        onContinue={() =>
          handleContinue({
            plan: 'teams',
            billingCycle: billingCycle,
            quantity: quantity,
          })
        }
        billingCycle={billingCycle}
        quantity={quantity}
        setBillingCycle={setBillingCycle}
        setQuantity={setQuantity}
        minimumQuantity={getTotalPaidUsers() ? getTotalPaidUsers() : 1}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-end mr-4 pr-4 mb-2">
        <Space align="center">
          <Text>Monthly</Text>
          <Switch
            checked={billingCycle === 'annual'}
            onChange={(checked) => {
              analytics.track(
                `Clicked Annual Billing Cycle To ${
                  checked ? 'Annual' : 'Monthly'
                } In Plan Selection`,
                {
                  email: user?.email,
                }
              );
              setBillingCycle(checked ? 'annual' : 'monthly');
            }}
            className={billingCycle === 'annual' ? 'bg-[#13C17F]' : ''}
          />
          <Text>
            Annual <Text type="success">(Save 20%)</Text>
          </Text>
        </Space>
      </div>
      <div className="mb-4">
        <Title level={2} className="text-center">
          Choose Your Plan
        </Title>
      </div>

      <Row gutter={[24, 24]}>
        {Object.entries(plans).map(([planKey, plan]) => {
          const displayPrice = getPlanPrice(
            plan.price,
            billingCycle === 'annual'
          );
          const finalPrice =
            billingCycle === 'annual'
              ? Math.ceil(displayPrice * 12)
              : displayPrice;
          const priceLabel =
            billingCycle === 'annual' ? '/clinician/year' : '/clinician/month';
          return (
            <Col xs={24} sm={24} md={8} key={planKey}>
              <Card
                className={`h-full ${
                  plan === planKey ? 'border-[#13C17F] border-2' : ''
                }`}
                hoverable
                onClick={() => handlePlanSelection(planKey)}
              >
                <div className="text-center mb-6">
                  <Title level={3}>{plan.name}</Title>
                  <Title
                    level={4}
                    className="text-[#13C17F] flex items-center justify-center"
                  >
                    {billingCycle === 'annual' && plan.price > 0 && (
                      <span className="text-gray-500 line-through mr-2 text-sm">
                        ${Math.ceil(plan.price * 12)}
                      </span>
                    )}
                    ${finalPrice}
                    <Text className="text-gray-500 text-sm">{priceLabel}</Text>
                  </Title>
                </div>

                <div className="text-center mt-6">
                  <Tooltip title={disablePlanTooltip(planKey)}>
                    <Button
                      type={plan === planKey ? 'primary' : 'default'}
                      size="large"
                      onClick={(e) => handleGetPlan(planKey, e)}
                      className={`w-full ${
                        plan === planKey
                          ? 'bg-[#13C17F] hover:bg-[#0F9F67] text-white'
                          : ''
                      }`}
                      style={{
                        height: '48px',
                        fontSize: '18px',
                        borderRadius: '12px',
                      }}
                      disabled={shouldDisablePlan(planKey)}
                    >
                      {'Get ' + plan.name}
                    </Button>
                  </Tooltip>
                </div>

                <List
                  dataSource={plan.features}
                  renderItem={(item) => (
                    <List.Item style={{ fontSize: '12px' }}>
                      <CheckCircleFilled
                        style={{ color: '#0F9F67', marginRight: '8px' }}
                      />
                      {item.feature}
                      <Popover content={item.description} trigger="hover">
                        <QuestionCircleOutlined
                          style={{ marginLeft: '8px', color: '#0F9F67' }}
                        />
                      </Popover>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default PlanSelection;
